import { useFlags } from "launchdarkly-react-client-sdk";
import { Suspense, useMemo } from "react";
import {
  Navigate,
  Routes as ReactRouterRoutes,
  Route,
  useLocation
} from "react-router-dom";

import { useAppSelector } from "app/hooks";
import {
  CHAT_UI_V2,
  CHUNK_LOAD_RECOVERY,
  CLEARTONE_RESTRICTIONS,
  DISABLE_REPORTS_AND_DASHBOARD,
  ERA_CLAIMS_TAB_FEATURE,
  FAXES_PORTAL_FEATURE,
  MARKETPLACE_FEATURE,
  NEW_ERROR_SCREEN,
  SCHEDULING_V3_RADAR_FEATURE,
  SCRIBE_FOLLOW_UPS_FEATURE,
  VOB_REQUEST_FEATURE
} from "constants/feature-flags";
import { VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS } from "constants/permissions";
import {
  ADMIN_LEVEL_GROUPS,
  GROUP_ADMINISTRATORS,
  GROUP_AUDIOLOGISTS,
  GROUP_BUSINESS_OWNERS,
  GROUP_SUNO
} from "constants/staff";
import Activate from "features/activate";
import { Layout, UnauthorizedLayout } from "features/app-layout";
import { SchedulerOfferBanner } from "features/app-layout/scheduler-choice/scheduler-offer-banner.component";
import { SchedulerRouteGate } from "features/app-layout/scheduler-choice/scheduler-route-gate.component";
import { useSchedulerChoice } from "features/app-layout/scheduler-choice/use-scheduler-choice.hook";
import {
  PRE_AUTH_USER_ID,
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentUser,
  selectLDIdentified
} from "features/auth/auth-slice";
import { BrowserNotificationsProvider } from "features/browser-notifications/browser-notifications.context";
import VoiceScreensRoute from "features/calls/voice-seat/voice-screens-route.component";
import { AppWithGlobalChat } from "features/chat/use-global-chat.hook";
import ForgotPassword from "features/forgot-password";
import InviteAccept from "features/invite";
import { LeadsProvider } from "features/leads-ui/use-leads.hook";
import Login from "features/login";
import { MedicalScribeSectionProvider } from "features/medical-scribe/medical-scribes-section/use-medical-scribe-section.hook";
import { MfaEnroll, MfaVerify } from "features/mfa";
import PasswordReset from "features/password-reset";
import { PatientDrawerProvider } from "features/patients/patient-drawer";
import { PatientHeaderProvider } from "features/patients/patient-header/use-patient-header.hook";
import { PatientNotesDrawerProvider } from "features/patients/patient-notes-drawer";
import { PatientStaffTasksDrawerProvider } from "features/patients/patient-staff-tasks-drawer";
import useSalesCoachRouteAccess from "features/sales-coach/hooks/use-sales-coach-route-access.hook";
import { StaffFilterProvider } from "features/scheduling/scheduler-side-sheet/filters/staff-filter/use-staff-filter.hook";
import { ClinicsFilterProvider } from "features/scheduling/scheduler-side-sheet/filters/use-clinics-filter.hook";
import SsoCallback from "features/sso-callback";
import { StaffTasksV2Provider } from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import { ClinicNotificationsProvider } from "hooks/use-clinic-notifications";
import { FaxProvider } from "hooks/use-fax.hook";
import { NewTouchpointProvider } from "hooks/use-new-touchpoint";
import useUserRoles from "hooks/use-user-roles.hook";
import { frontendLog } from "utils/debug.utils";
import {
  lazyWithRetry,
  setChunkLoadRecoveryEnabled
} from "utils/lazy-with-retry.util";

import ConfirmDialog from "../components/confirm-dialog";
import { ProductPictureViewer } from "../components/product-picture-icon";
import { FaxesListProvider } from "../features/faxes/faxes-filters/use-faxes-list.hook";
import { useGetMySendBirdDetailsQuery } from "../services/users";
import { RouteErrorBoundary as NewRouteErrorBoundary } from "./new-route-error-boundary.component";
import PermissionPage from "./permission-page.component";
import ProtectedRoute from "./protected-route.component";
import ReportsOnlyGuard from "./reports-only-guard.component";
import RequireAuth from "./required-auth";
import { RouteErrorBoundary as PreviousRouteErrorBoundary } from "./route-error-boundary.component";
import { RouteSuspenseFallback } from "./route-suspense-fallback.component";

// Lazy loaded routes
const SchedulingRoutesLazy = lazyWithRetry(
  () => import("features/scheduling/scheduling.routes"),
  "scheduling.routes"
);

// Scheduling V3 — the greenfield scheduling module (kickoff 2026-09-14). Its own chunk;
// reachable by URL only until the opt-in setting ships.
const SchedulingV3RoutesLazy = lazyWithRetry(
  () => import("features/scheduling-v3/scheduling-v3.routes"),
  "scheduling-v3.routes"
);

// Patient Review Mode. Its own chunk: it is only ever reached by opening the
// separate review window, so no staff page should pay for it.
const PatientReviewPageLazy = lazyWithRetry(
  () =>
    import("features/patients/patient-review/patient-review-page.component"),
  "patient-review-page"
);

const McpAuthorizePageLazy = lazyWithRetry(
  () => import("features/mcp-authorize/mcp-authorize-page.component"),
  "mcp-authorize-page"
);

const LeadsUIRoutesLazy = lazyWithRetry(
  () => import("features/leads-ui/leads-ui.routes"),
  "leads-ui.routes"
);

const BillingRoutesLazy = lazyWithRetry(
  () => import("features/billing/billing.routes"),
  "billing.routes"
);

const CollectionsRoutesLazy = lazyWithRetry(
  () => import("features/collections/collections.routes"),
  "collections.routes"
);

const SalesCoachRoutesLazy = lazyWithRetry(
  () => import("features/sales-coach/sales-coach.routes"),
  "sales-coach.routes"
);

const CallsRoutesLazy = lazyWithRetry(
  () => import("features/calls/calls.routes"),
  "calls.routes"
);

const ChatLazy = lazyWithRetry(
  () => import("features/chat/chat.component"),
  "chat.component"
);

const ChatV2Lazy = lazyWithRetry(
  () => import("features/chat/v2/chat-v2.component"),
  "chat-v2.component"
);

const DashboardRoutesLazy = lazyWithRetry(
  () => import("features/dashboard/dashboard.routes"),
  "dashboard.routes"
);

const InboxRoutesLazy = lazyWithRetry(
  () => import("features/inbox/inbox.routes"),
  "inbox.routes"
);

const InventoryRoutesLazy = lazyWithRetry(
  () => import("features/inventory/inventory.routes"),
  "inventory.routes"
);

const MarketingRoutesLazy = lazyWithRetry(
  () => import("features/marketing/marketing.routes"),
  "marketing.routes"
);

const MarketplaceRoutesLazy = lazyWithRetry(
  () => import("features/marketplace/marketplace.routes"),
  "marketplace.routes"
);

const MedicalScribesListLazy = lazyWithRetry(
  () =>
    import(
      "features/medical-scribe/medical-scribes-section/medical-scribes-section.component"
    ),
  "medical-scribes-section.component"
);

const FollowUpsRoutesLazy = lazyWithRetry(
  () => import("features/follow-ups/follow-ups.routes"),
  "follow-ups.routes"
);

const PatientsRoutesLazy = lazyWithRetry(
  () => import("features/patients/patients.routes"),
  "patients.routes"
);

const QuickbooksRoutesLazy = lazyWithRetry(
  () => import("features/quickbooks/quickbooks.routes"),
  "quickbooks.routes"
);

const ReviewsRoutesLazy = lazyWithRetry(
  () => import("features/reviews/reviews.routes"),
  "reviews.routes"
);

const PodiumOauthCallbackLazy = lazyWithRetry(
  () => import("features/podium/podium-oauth-callback.component"),
  "podium-oauth-callback.component"
);

const ZoomOauthCallbackLazy = lazyWithRetry(
  () => import("features/settings/zoom/zoom-oauth-callback.component"),
  "zoom-oauth-callback.component"
);

const GoogleReviewsOauthCallbackLazy = lazyWithRetry(
  () =>
    import(
      "features/settings/google-reviews/google-reviews-oauth-callback.component"
    ),
  "google-reviews-oauth-callback.component"
);

const ReportsRoutesLazy = lazyWithRetry(
  () => import("features/reports/reports.routes"),
  "reports.routes"
);

const SettingsRoutesLazy = lazyWithRetry(
  () => import("features/settings/settings.routes"),
  "settings.routes"
);

const PracticeSettingsRoutesLazy = lazyWithRetry(
  () => import("features/practice-settings/practice-settings.routes"),
  "practice-settings.routes"
);

const AdministrationRoutesLazy = lazyWithRetry(
  () => import("../features/administration/administration.routes"),
  "administration.routes"
);

// Import the component directly, not the directory index — a bare
// `../features/faxes` import lets Rollup name the chunk from index.ts's own
// basename ("index"), colliding with five other directory-index chunks and
// silently defeating createRetryingImport's chunkName-match gate (ENG-1830
// review): "index-<hash>.js" never contains "faxes", so the retry never
// fires for this route. chunkName must match the emitted basename exactly
// ("faxes.component", not just "faxes") — the bare substring "faxes" also
// matches faxes.utils-<hash>.js, a real dependency chunk this route imports
// transitively (faxes.component -> faxes-list.component -> utils/faxes.utils),
// which would pass the gate and hand React.lazy the wrong module.
const FaxesLazy = lazyWithRetry(
  () => import("../features/faxes/faxes.component"),
  "faxes.component"
);

const InsuranceRoutesLazy = lazyWithRetry(
  () => import("../features/insurance/insurance.routes"),
  "insurance.routes"
);

const VobListingRoutesLazy = lazyWithRetry(
  () => import("features/vob-listing/vob-listing.routes"),
  "vob-listing.routes"
);

const HomeLazy = lazyWithRetry(
  () => import("features/home/home.component"),
  "home.component"
);

const OnboardingHubLazy = lazyWithRetry(
  () => import("features/onboarding-hub/onboarding-hub.component"),
  "onboarding-hub.component"
);

const InternalDocumentsRoutesLazy = lazyWithRetry(
  () => import("features/internal-documents/internal-documents.routes"),
  "internal-documents.routes"
);
const PracticeSessionsPageLazy = lazyWithRetry(
  () => import("features/enablement/practice-sessions-page.component"),
  "practice-sessions-page.component"
);

export default function Routes() {
  const flags = useFlags();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const { isScheduler, canAccessDashboards, canAccessQBO, isReportsOnlyUser } =
    useUserRoles();
  const launchDarklyIdentified = useAppSelector(selectLDIdentified);
  const location = useLocation();

  const isNewErrorScreenEnabled = flags[NEW_ERROR_SCREEN] ?? false;
  const isSchedulingV3RadarEnabled =
    flags[SCHEDULING_V3_RADAR_FEATURE] ?? false;

  // The lazy import factories run outside React, so the flag is synced into
  // the lazy-with-retry module instead of being read via useFlags there.
  // Synced during render (idempotent), not in an effect: a route chunk can
  // fail while the first post-flags render is still committing, before
  // effects flush. Known limitation either way: a chunk failing before
  // LaunchDarkly resolves sees the flag as false and goes straight to the
  // error boundary (no auto-reload).
  setChunkLoadRecoveryEnabled(flags[CHUNK_LOAD_RECOVERY] ?? false);

  const RouteErrorBoundary = useMemo(() => {
    return isNewErrorScreenEnabled
      ? NewRouteErrorBoundary
      : PreviousRouteErrorBoundary;
  }, [isNewErrorScreenEnabled]);

  const { data: sendbirdDetails, isLoading: isSendbirdDetailsLoading } =
    useGetMySendBirdDetailsQuery(undefined, {
      skip: !currentUser?.sendbird_user_id
    });

  // Financial-only users lose the admin panel outright — the access-tab toggle also turns
  // has_access_to_admin_panel off, this is the belt to that suspender.
  const isAdministrationPageEnabled =
    (currentUser?.has_access_to_admin_panel ?? true) && !isReportsOnlyUser;
  const isERAClaimsTabEnabled = flags[ERA_CLAIMS_TAB_FEATURE] ?? true;
  const isCleartoneRestrictionsEnabled = flags[CLEARTONE_RESTRICTIONS];
  const isFaxesPortalFeatureEnabled = flags[FAXES_PORTAL_FEATURE] ?? true;
  const isMarketplaceFeatureEnabled = flags[MARKETPLACE_FEATURE] ?? false;
  const hasActiveProviderIntegration =
    currentClinic?.has_active_provider_integration ||
    currentPractice?.has_active_provider_integration;
  const isVobRequestFeatureEnabled =
    (flags[VOB_REQUEST_FEATURE] ?? false) && hasActiveProviderIntegration;
  const isGoogleReviewsEnabled =
    Boolean(currentPractice?.google_reviews_enabled) &&
    Boolean(currentUser?.has_access_to_google_reviews);
  const isReportsAndDashboardDisabled =
    flags[DISABLE_REPORTS_AND_DASHBOARD] ?? false;
  // Both halves come from the section itself, because deciding this needs one fact
  // `flags[...] ?? false` cannot express: whether LaunchDarkly has answered YET.
  // `ldIdentified` alone does not mean it has. See the hook.
  const salesCoachAccess = useSalesCoachRouteAccess();
  // SN-19269. Defaults OFF — there is no backend gate, so this is the only
  // thing keeping the Follow Ups surface unreachable.
  const isFollowUpsEnabled = flags[SCRIBE_FOLLOW_UPS_FEATURE] ?? false;
  // ENG-1981: which scheduler opens is the person's own choice (the flag only offers it).
  // Read here, at the top, so the request starts at boot rather than on first navigation.
  useSchedulerChoice();

  // True once the real user has replaced the pre-auth placeholder. Routes
  // gated on roles/permissions must render a fallback (not a redirect) until
  // then — on a hard refresh the role data is empty at first render, and a
  // premature <Navigate> permanently rewrites the URL (ENG-1668, same class
  // of bug as ENG-664 on Collections).
  const isUserLoaded = Boolean(
    currentUser && currentUser.id !== PRE_AUTH_USER_ID
  );

  const inAppChatFullyEnabled =
    (currentPractice?.sendbird_chat_enabled &&
      currentUser?.sendbird_user_id &&
      sendbirdDetails?.access_token) ??
    false;
  // Chat's gate depends on practice + user + the Sendbird details fetch.
  // While any of them is in flight, "disabled" is indistinguishable from
  // "not loaded yet", so the chat route must hold a fallback instead of
  // redirecting away.
  const isChatGateResolving =
    !isUserLoaded ||
    !currentPractice ||
    (Boolean(
      currentPractice.sendbird_chat_enabled && currentUser?.sendbird_user_id
    ) &&
      isSendbirdDetailsLoading);

  frontendLog(["feature-flags", JSON.parse(JSON.stringify(flags))]);
  return (
    <ReactRouterRoutes>
      <Route element={<UnauthorizedLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/password-reset/:uid/:token" element={<PasswordReset />} />
        <Route path="/activate/:uid/:token" element={<Activate />} />
        {/* Public invite acceptance — invitee isn't logged in yet. */}
        <Route path="/accept/:token" element={<InviteAccept />} />
        {/* MFA challenge step (ENG-1096) — pre-auth pages guarded by
            mfaPending state; each redirects to /login when no challenge. */}
        <Route path="/mfa/verify" element={<MfaVerify />} />
        <Route path="/mfa/enroll" element={<MfaEnroll />} />
      </Route>
      {/* SSO landing — the backend redirects here with the result in the URL
          hash (#auth_token= on success, #sso_error= on failure). One component
          serves both. Runs BEFORE auth exists, so it must NOT be RequireAuth-
          wrapped and is OUTSIDE UnauthorizedLayout so the layout's post-login
          auto-redirect can't fire before the fail-closed practice check. */}
      {/* Patient Review Mode — opened with window.open onto a patient-facing
          screen, so it is authenticated but deliberately OUTSIDE <Layout />:
          no side menu, no patient header, no quick launcher, nothing the
          patient could navigate with. */}
      <Route
        path="/patient-review/:patientId"
        element={
          <RequireAuth>
            <RouteErrorBoundary routeName="Patient Review">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <PatientReviewPageLazy />
              </Suspense>
            </RouteErrorBoundary>
          </RequireAuth>
        }
      />
      {/* AI assistant (MCP) consent. Authenticated but outside <Layout />: the page is
          one decision, reached from another app. RequireAuth sends a signed-out user to /login
          with the full URL (query included) as `from`, and login brings them back here. Not
          flag-gated: the backend 404s every /api/mcp/ call while MCP is off. */}
      <Route
        path="/mcp/authorize"
        element={
          <RequireAuth>
            <RouteErrorBoundary routeName="AI Assistant Consent">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <McpAuthorizePageLazy />
              </Suspense>
            </RouteErrorBoundary>
          </RequireAuth>
        }
      />
      <Route path="/sso/complete" element={<SsoCallback />} />
      <Route path="/sso/error" element={<SsoCallback />} />
      {/* Always register OAuth callback routes.
          Feature flags can be undefined during initial load, and if this route
          isn't registered, the "/" route + its "*" child can catch "/oauth/..."
          and redirect away before the callback runs. */}
      <Route
        path="/oauth/podium/callback"
        element={
          <RequireAuth>
            <RouteErrorBoundary routeName="Podium OAuth">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <PodiumOauthCallbackLazy />
              </Suspense>
            </RouteErrorBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="/oauth/zoom/callback"
        element={
          <RequireAuth>
            <RouteErrorBoundary routeName="Zoom OAuth">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <ZoomOauthCallbackLazy />
              </Suspense>
            </RouteErrorBoundary>
          </RequireAuth>
        }
      />
      <Route
        path="/oauth/google-reviews/callback"
        element={
          <RequireAuth>
            <RouteErrorBoundary routeName="Google Reviews OAuth">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <GoogleReviewsOauthCallbackLazy />
              </Suspense>
            </RouteErrorBoundary>
          </RequireAuth>
        }
      />
      {isAdministrationPageEnabled && (
        <Route
          path="administration/*"
          element={
            <RequireAuth>
              <>
                <RouteErrorBoundary routeName="Administration">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <ClinicNotificationsProvider>
                      <AdministrationRoutesLazy />
                    </ClinicNotificationsProvider>
                  </Suspense>
                </RouteErrorBoundary>
                <ConfirmDialog />
              </>
            </RequireAuth>
          }
        />
      )}

      {!isReportsOnlyUser && (
        <Route
          path="leads/*"
          element={
            <RouteErrorBoundary routeName="Leads">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <RequireAuth>
                  <ProtectedRoute>
                    <LeadsProvider>
                      <LeadsUIRoutesLazy />
                      <ConfirmDialog />
                    </LeadsProvider>
                  </ProtectedRoute>
                </RequireAuth>
              </Suspense>
            </RouteErrorBoundary>
          }
        />
      )}

      <Route
        path="/"
        element={
          <RequireAuth>
            <PatientHeaderProvider>
              <PatientDrawerProvider>
                <ClinicNotificationsProvider>
                  <StaffTasksV2Provider>
                    <PatientNotesDrawerProvider>
                      <PatientStaffTasksDrawerProvider>
                        <FaxProvider>
                          <NewTouchpointProvider>
                            <ReportsOnlyGuard>
                              <BrowserNotificationsProvider>
                                {currentPractice?.sendbird_chat_enabled &&
                                currentUser?.sendbird_user_id ? (
                                  <AppWithGlobalChat>
                                    <Layout />
                                  </AppWithGlobalChat>
                                ) : (
                                  <Layout />
                                )}
                              </BrowserNotificationsProvider>
                            </ReportsOnlyGuard>
                            <ConfirmDialog />
                            <ProductPictureViewer />
                          </NewTouchpointProvider>
                        </FaxProvider>
                      </PatientStaffTasksDrawerProvider>
                    </PatientNotesDrawerProvider>
                  </StaffTasksV2Provider>
                </ClinicNotificationsProvider>
              </PatientDrawerProvider>
            </PatientHeaderProvider>
          </RequireAuth>
        }
      >
        <Route path="permission" element={<PermissionPage />} />
        <Route
          path=""
          element={
            currentPractice ? (
              <Navigate
                to={{
                  // Financial-only users land straight on Reports — their only section.
                  pathname: isReportsOnlyUser ? "reports" : "home",
                  search: location.search
                }}
                replace
              />
            ) : (
              <RouteSuspenseFallback />
            )
          }
        />
        {/* Single /home route owned by HomeLazy, registered unconditionally
            (the homeScreen flag was retired in ENG-1668). Dashboard widgets
            render as an "At a Glance" tab inside HomeContent when both the
            LaunchDarkly DASHBOARD_WIDGETS_FEATURE flag and
            Practice.enable_dashboard_widgets are true. Per-role widget
            visibility is enforced by the backend. */}
        <Route
          path="home"
          element={
            <RouteErrorBoundary routeName="Home">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <HomeLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        <Route
          path="onboarding/*"
          element={
            <RouteErrorBoundary routeName="Onboarding">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <OnboardingHubLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        <Route
          path="sops/*"
          element={
            <RouteErrorBoundary routeName="SOPs">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <InternalDocumentsRoutesLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        {/* Standalone Practice Sessions surface (ENG-1832) — independent of the Onboarding
            route's implementation-status gate above. Registered unconditionally, matching the
            /onboarding pattern; the training_enabled check happens inside the component. */}
        <Route
          path="practice-sessions"
          element={
            <RouteErrorBoundary routeName="Practice Sessions">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <PracticeSessionsPageLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        {/* Registered unconditionally so a refresh on /dashboard can't fall
            into the "*" catch-all while roles are still loading; the access
            decision happens in the element once the user has loaded. */}
        <Route
          path="dashboard/*"
          element={
            !isUserLoaded ? (
              <RouteSuspenseFallback />
            ) : !isReportsAndDashboardDisabled && canAccessDashboards ? (
              <RouteErrorBoundary routeName="Dashboard">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <DashboardRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        {!isScheduler && (
          <Route
            path="inbox/*"
            element={
              <RouteErrorBoundary routeName="Inbox">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <InboxRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler && (
          <Route
            path="inventory/*"
            element={
              <RouteErrorBoundary routeName="Inventory">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  {isCleartoneRestrictionsEnabled ? (
                    <ProtectedRoute roles={ADMIN_LEVEL_GROUPS}>
                      <InventoryRoutesLazy />
                    </ProtectedRoute>
                  ) : (
                    <InventoryRoutesLazy />
                  )}
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler && !isReportsAndDashboardDisabled && (
          <Route
            path="reports/*"
            element={
              <ProtectedRoute
                roles={ADMIN_LEVEL_GROUPS}
                permissions={[VIEW_OWN_REPORTS_ONLY, VIEW_REPORTS]}
              >
                <RouteErrorBoundary routeName="Reports">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <ReportsRoutesLazy />
                  </Suspense>
                </RouteErrorBoundary>
              </ProtectedRoute>
            }
          />
        )}
        <Route
          path="scheduling-v3/*"
          element={
            <SchedulerRouteGate target="v3">
              <RouteErrorBoundary routeName="Scheduling V3">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <SchedulingV3RoutesLazy
                    radarEnabled={isSchedulingV3RadarEnabled}
                  />
                </Suspense>
              </RouteErrorBoundary>
            </SchedulerRouteGate>
          }
        />
        <Route
          path="scheduling/*"
          element={
            <SchedulerRouteGate target="classic">
              {/* ENG-1981: the offer lives in the shell — the classic module is frozen. */}
              <SchedulerOfferBanner>
                <RouteErrorBoundary routeName="Scheduling">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <ClinicsFilterProvider>
                      <StaffFilterProvider>
                        <SchedulingRoutesLazy />
                      </StaffFilterProvider>
                    </ClinicsFilterProvider>
                  </Suspense>
                </RouteErrorBoundary>
              </SchedulerOfferBanner>
            </SchedulerRouteGate>
          }
        />
        {!isScheduler && (
          <Route
            path="billing/*"
            element={
              <RouteErrorBoundary routeName="Billing">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  {isCleartoneRestrictionsEnabled ? (
                    <ProtectedRoute roles={ADMIN_LEVEL_GROUPS}>
                      <BillingRoutesLazy />
                    </ProtectedRoute>
                  ) : (
                    <BillingRoutesLazy />
                  )}
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler &&
          (location.pathname.startsWith("/collections") ||
            currentUser?.has_access_to_collections) && (
            <Route
              path="collections/*"
              element={
                <RouteErrorBoundary routeName="Collections">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <CollectionsRoutesLazy />
                  </Suspense>
                </RouteErrorBoundary>
              }
            />
          )}
        {!isScheduler && isERAClaimsTabEnabled && (
          <Route
            path="eras/*"
            element={
              <RouteErrorBoundary routeName="Insurance">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  {isCleartoneRestrictionsEnabled ? (
                    <ProtectedRoute roles={ADMIN_LEVEL_GROUPS}>
                      <InsuranceRoutesLazy />
                    </ProtectedRoute>
                  ) : (
                    <InsuranceRoutesLazy />
                  )}
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {(!launchDarklyIdentified ||
          isVobRequestFeatureEnabled ||
          location.pathname.startsWith("/vobs")) && (
          <Route
            path="vobs/*"
            element={
              <RouteErrorBoundary routeName="VOBs">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <VobListingRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        <Route
          path="patients/*"
          element={
            <RouteErrorBoundary routeName="Patients">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <PatientsRoutesLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        <Route
          path="settings/*"
          element={
            <RouteErrorBoundary routeName="Settings">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <SettingsRoutesLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        <Route
          path="practice-settings/*"
          element={
            <RouteErrorBoundary routeName="PracticeSettings">
              <Suspense fallback={<RouteSuspenseFallback />}>
                <PracticeSettingsRoutesLazy />
              </Suspense>
            </RouteErrorBoundary>
          }
        />
        {!isScheduler && (
          <Route
            path="marketing/*"
            element={
              <RouteErrorBoundary routeName="Marketing">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <MarketingRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler && (
          <Route
            path="calls/*"
            element={
              <VoiceScreensRoute>
                <RouteErrorBoundary routeName="Calls">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <CallsRoutesLazy />
                  </Suspense>
                </RouteErrorBoundary>
              </VoiceScreensRoute>
            }
          />
        )}
        {!isScheduler && canAccessQBO && (
          <Route
            path="quickbooks/*"
            element={
              <RouteErrorBoundary routeName="QuickBooks">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <QuickbooksRoutesLazy />
                </Suspense>
              </RouteErrorBoundary>
            }
          />
        )}
        {!isScheduler && (!currentPractice || isGoogleReviewsEnabled) && (
          <Route
            path="reviews/*"
            element={
              <ProtectedRoute
                roles={[
                  GROUP_BUSINESS_OWNERS,
                  GROUP_ADMINISTRATORS,
                  GROUP_AUDIOLOGISTS
                ]}
              >
                <RouteErrorBoundary routeName="Reviews">
                  <Suspense fallback={<RouteSuspenseFallback />}>
                    <ReviewsRoutesLazy />
                  </Suspense>
                </RouteErrorBoundary>
              </ProtectedRoute>
            }
          />
        )}
        {!isScheduler &&
          (!launchDarklyIdentified ||
            isFaxesPortalFeatureEnabled ||
            location.pathname.startsWith("/faxes-portal")) && (
            <Route
              path="faxes-portal/*"
              element={
                <FaxesListProvider>
                  <RouteErrorBoundary routeName="Faxes">
                    <Suspense fallback={<RouteSuspenseFallback />}>
                      <FaxesLazy />
                    </Suspense>
                  </RouteErrorBoundary>
                </FaxesListProvider>
              }
            />
          )}
        {!isScheduler &&
          (!launchDarklyIdentified ||
            isMarketplaceFeatureEnabled ||
            location.pathname.startsWith("/marketplace")) && (
            <Route
              path="marketplace/*"
              element={
                <ProtectedRoute roles={[GROUP_BUSINESS_OWNERS]}>
                  <RouteErrorBoundary routeName="Marketplace">
                    <Suspense fallback={<RouteSuspenseFallback />}>
                      <MarketplaceRoutesLazy />
                    </Suspense>
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
          )}
        <Route
          path="medical-scribes/*"
          element={
            <MedicalScribeSectionProvider>
              <RouteErrorBoundary routeName="Medical Scribes">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  <MedicalScribesListLazy />
                </Suspense>
              </RouteErrorBoundary>
            </MedicalScribeSectionProvider>
          }
        />
        {/* The `!launchDarklyIdentified ||` and pathname clauses are not belt
            and braces: on a hard refresh of a deep link the flags are empty at
            first render, and a premature gate permanently rewrites the URL
            (ENG-1668, and ENG-664 before it on Collections). */}
        {!isScheduler &&
          (!launchDarklyIdentified ||
            isFollowUpsEnabled ||
            location.pathname.startsWith("/action-items")) && (
            <Route
              path="action-items/*"
              element={
                <ProtectedRoute
                  roles={[
                    GROUP_BUSINESS_OWNERS,
                    GROUP_SUNO,
                    GROUP_AUDIOLOGISTS
                  ]}
                >
                  <RouteErrorBoundary routeName="Action Items">
                    <Suspense fallback={<RouteSuspenseFallback />}>
                      <FollowUpsRoutesLazy />
                    </Suspense>
                  </RouteErrorBoundary>
                </ProtectedRoute>
              }
            />
          )}
        {/* Registered unconditionally with the decision made in the element,
            the /dashboard pattern above — NOT the conditional-registration one
            used for action-items. Both avoid the "*" catch-all eating a deep
            link before LaunchDarkly resolves, but that one keeps the route
            whenever the pathname matches, which means typing /sales-coach
            reaches the page with the flag off — and the flag must stay the gate.

            ⚠️ The element holds a fallback until the flag has a REAL value, not
            merely until `ldIdentified` is true. Those are different moments, and
            redirecting in the gap answered a deep link by rewriting the URL with
            `replace` — same bug class as ENG-1668 and ENG-664 above. The
            distinction lives in `useSalesCoachRouteAccess`. */}
        {!isScheduler && (
          <Route
            path="sales-coach/*"
            element={
              salesCoachAccess.isResolving ? (
                <RouteSuspenseFallback />
              ) : salesCoachAccess.isEnabled ? (
                <ProtectedRoute
                  roles={[
                    GROUP_BUSINESS_OWNERS,
                    GROUP_SUNO,
                    GROUP_AUDIOLOGISTS
                  ]}
                >
                  <RouteErrorBoundary routeName="Sales Coach">
                    <Suspense fallback={<RouteSuspenseFallback />}>
                      <SalesCoachRoutesLazy />
                    </Suspense>
                  </RouteErrorBoundary>
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        )}
        {/* Registered unconditionally so a refresh on /chat can't fall into
            the "*" catch-all while the practice/user/Sendbird chain is still
            loading; the access decision happens in the element once the gate
            data has resolved. */}
        <Route
          path="chat/*"
          element={
            isChatGateResolving ? (
              <RouteSuspenseFallback />
            ) : !isScheduler && inAppChatFullyEnabled ? (
              <RouteErrorBoundary routeName="Chat">
                <Suspense fallback={<RouteSuspenseFallback />}>
                  {(flags[CHAT_UI_V2] ?? false) ? <ChatV2Lazy /> : <ChatLazy />}
                </Suspense>
              </RouteErrorBoundary>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </ReactRouterRoutes>
  );
}
